import {getTrackerConfigErrors, getTrackerPayloadErrors, createTrackerSummaryInput, validateTrackerWorkInput} from "./trackerLifecycleHelpers.js";
import {normalizeSudsConfig, getSudsConfigErrors, normalizeSudsPayload, getSudsPayloadErrors, summarizeSudsSeries} from "./sudsSeriesHelpers.js";
import {normalizeMomentaryConfig, getMomentaryConfigErrors, getMomentarySchedule, normalizeMomentaryPayload, updateMomentaryStatus, advanceMomentarySession, summarizeMomentarySession, getMomentaryPayloadErrors} from "./momentarySamplingHelpers.js";
import {
	normalizeEpisodeConfig, createEpisodePayload, summarizeEpisodes, formatEpisodeDate,
	sortEpisodeEntries, groupEpisodeEntries, getEpisodeConfigErrors, getEpisodeEntryErrors,
} from "./trackerEntryHelpers.js";
import BaseToolEngine from "@shared/GenericTools/engines/BaseToolEngine";
import FormToolEngine from "@shared/GenericTools/engines/FormToolEngine";
import type {
	EnginePayload,
	ToolConfig,
	TrackerSchemaEngineInterface,
	TrackerEntryEngineInterface,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import {
	TrackerSchemaEngineInterfaceToken,
	TrackerEntryEngineInterfaceToken,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import {
	ToolEngineOptions,
	ToolEngineValidationResult,
	type FormModelInterface,
	type ToolEngineDataValue,
	type ToolInstanceSnapshotData,
	type ToolResultSnapshotData,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineDataTypes";

export default class TrackerToolEngine extends BaseToolEngine implements TrackerSchemaEngineInterface, TrackerEntryEngineInterface {
	formEngine: FormToolEngine


	isWorkNormalizationStrict(config: ToolConfig): boolean { return ["episodeLog", "momentarySampling", "sudsSeries"].includes(String(config.trackerKind)) }
	getWorkNormalizationScope(): string { return "trackerNormalization" }

	validateWorkInput(payload: EnginePayload, config: ToolConfig): void {
		if (this.isWorkNormalizationStrict(config)) validateTrackerWorkInput(payload, config)
	}

	normalizeWorkPayload(payload: EnginePayload, config: ToolConfig): EnginePayload {
		return this.normalizePayload(payload, config)
	}

	finalizeWorkSubmission(payload: EnginePayload, config: ToolConfig): EnginePayload {
		if (config.trackerKind === "momentarySampling") payload = this.advanceMomentarySession(payload, config)
		if (config.trackerKind === "sudsSeries") this.assertWorkValidation(this.validateConfig(config))
		if (["episodeLog", "momentarySampling", "sudsSeries"].includes(String(config.trackerKind))) this.assertWorkValidation(this.validateTrackerPayload(payload, config, true))
		return payload
	}

	validateWorkPayload(payload: EnginePayload, config: ToolConfig): ToolEngineValidationResult {
		return this.validateTrackerPayload(payload, config)
	}

	getEngineInterfaceTokens() {
		return [
			TrackerSchemaEngineInterfaceToken,
			TrackerEntryEngineInterfaceToken,
		]
	}

	constructor(formEngine: FormToolEngine) {
		super()
		this.formEngine = formEngine
	}


	// Preserve the variant's existing structural operations behind one lifecycle.
	normalizeConfig(config: ToolConfig = {}): ToolConfig {
		if (config.trackerKind === "sudsSeries") return this.normalizeSudsConfig(config)
		if (config.trackerKind === "momentarySampling") return this.normalizeMomentaryConfig(config)
		if (config.trackerKind === "episodeLog") return this.normalizeEpisodeConfig(config)
		if (config.trackerKind === "weeklyDiary") return this.createWeeklyDiaryConfigFromToolConfig(config)
		return this.createTrackerConfigFromToolConfig(config)
	}

	createDefaultPayload(config: ToolConfig = {}): EnginePayload {
		const tracker = this.normalizeConfig(config)
		if (tracker.trackerKind === "sudsSeries") return this.normalizeSudsPayload({}, tracker)
		if (tracker.trackerKind === "momentarySampling") return this.normalizeMomentaryPayload({}, tracker)
		if (tracker.trackerKind === "episodeLog") return this.createEpisodePayload({}, tracker, [])
		if (tracker.trackerKind === "weeklyDiary") {
			const entries = (tracker.days as ToolConfig[]).map((day, index) => this.createWeeklyDiaryEntry(tracker, {}, index))
			return this.createWeeklyDiaryEnginePayload({}, tracker, entries)
		}
		return {tracker, entries: []}
	}

	normalizePayload(payload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		if (config.trackerKind === "sudsSeries") return this.normalizeSudsPayload(payload, config)
		if (config.trackerKind === "momentarySampling") return this.normalizeMomentaryPayload(payload, config)
		if (config.trackerKind === "episodeLog") {
			return this.createEpisodePayload(payload, config, (payload.entries || []) as ToolConfig[])
		}
		// Existing non-episode snapshots are historical data. Do not regenerate
		// dates, reorder days, discard extra answers, or convert invalid drafts to valid ones.
		return {
			...payload,
			tracker: payload.tracker || this.normalizeConfig(config),
			entries: payload.entries || [],
		}
	}

	validateConfig(config: ToolConfig = {}): ToolEngineValidationResult {
		const result = new ToolEngineValidationResult()
		result.errors = config.trackerKind === "sudsSeries" ? getSudsConfigErrors(config) : config.trackerKind === "momentarySampling" ? getMomentaryConfigErrors(config) : config.trackerKind === "episodeLog" ? getEpisodeConfigErrors(config) : getTrackerConfigErrors(config)
		result.isValid = result.errors.length === 0
		return result
	}

	validateTrackerPayload(payload: EnginePayload = {}, config: ToolConfig = {}, isSubmission = false): ToolEngineValidationResult {
		const result = new ToolEngineValidationResult()
		if (config.trackerKind === "sudsSeries") {
			result.errors = getSudsPayloadErrors(payload, config, isSubmission)
			result.isValid = result.errors.length === 0
			return result
		}
		if (config.trackerKind === "momentarySampling") {
			result.errors = getMomentaryPayloadErrors(payload, config)
			result.isValid = result.errors.length === 0
			return result
		}
		const tracker = payload.tracker as ToolConfig | undefined
		const entries = payload.entries as ToolConfig[] | undefined
		if (!tracker || !Array.isArray(entries) || !entries.length) {
			result.errors = ["Invalid tracker engine payload"]
		} else if (tracker.trackerKind === "episodeLog") {
			result.errors = this.validateConfig(tracker).errors
			if (!result.errors.length) result.errors = getEpisodeEntryErrors(entries, tracker)
		} else {
			result.errors = getTrackerPayloadErrors(payload, config)
		}
		result.isValid = result.errors.length === 0
		return result
	}

	createSummaryInput(payload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		if (config.trackerKind === "sudsSeries") return {headline: config.title || "SUDS", series: this.summarizeSudsSeries(payload, config)}
		if (config.trackerKind === "momentarySampling") return this.summarizeMomentarySession(payload, config)
		const tracker = config.trackerKind === "episodeLog" ? config : (payload.tracker || config) as ToolConfig
		if (tracker.trackerKind !== "episodeLog") return createTrackerSummaryInput(payload, tracker)
		const normalized = this.normalizePayload(payload, tracker)
		return {
			headline: tracker.title,
			tracker: normalized.tracker,
			entries: normalized.entries,
			...this.summarizeEpisodes(normalized.entries as ToolConfig[], tracker),
		}
	}

	normalizeEpisodeConfig(config: ToolConfig = {}): ToolConfig {
		return normalizeEpisodeConfig(config)
	}

	formatEpisodeDate(value: string | Date | null): string {
		return formatEpisodeDate(value)
	}

	createEpisodePayload(payload: EnginePayload = {}, config: ToolConfig = {}, entries: ToolConfig[] = [], options: {includeEmpty?: boolean} = {}): EnginePayload {
		return createEpisodePayload(payload, config, entries, options)
	}

	summarizeEpisodes(entries: ToolConfig[] = [], config: ToolConfig = {}): ToolConfig {
		return summarizeEpisodes(entries, config)
	}

	sortEpisodeEntries(entries: ToolConfig[], config: ToolConfig, sortBy = "chronological"): ToolConfig[] {
		return sortEpisodeEntries(entries, config, sortBy)
	}

	groupEpisodeEntries(entries: ToolConfig[], groupings: Record<string, string> = {}): {label: string, entries: ToolConfig[]}[] {
		return groupEpisodeEntries(entries, groupings)
	}

	validateEpisodeEntries(entries: ToolConfig[] = [], config: ToolConfig = {}): ToolEngineValidationResult {
		const result = new ToolEngineValidationResult()
		result.errors = getEpisodeEntryErrors(entries, config)
		result.isValid = result.errors.length === 0
		return result
	}

	getTrackerTemplateSummaryInput(config: ToolConfig = {}): ToolConfig {
		if (config.trackerKind === "sudsSeries") return {pointCount: (config.points as ToolConfig[] || []).length}
		if (config.trackerKind === "momentarySampling") return {momentCount: this.getMomentarySchedule(config).length}
		if (config.trackerKind === "weeklyDiary") {
			return {
				dayCount: config.weekLength || 7,
				rowCount: [config.targetBehaviors, config.contextFields, config.skills].reduce((count: number, items) => count + (Array.isArray(items) ? items.length : 0), 0),
			}
		}
		const entryForm = (config.entryForm || {}) as ToolConfig
		const fields = (config.trackerKind === "episodeLog" ? this.normalizeEpisodeConfig(config).fields : entryForm.fields || entryForm.formElements || []) as ToolConfig[]
		return {fieldCount: fields.filter(field => !["TITLE", "FOOTER", "SCORE_RESULT", "SCORE_BUCKET_RESULT"].includes(String(field.type))).length}
	}

	normalizeMomentaryConfig(config: ToolConfig = {}): ToolConfig { return normalizeMomentaryConfig(config) }
	normalizeSudsConfig(config: ToolConfig = {}): ToolConfig { return normalizeSudsConfig(config) }
	normalizeSudsPayload(payload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload { return normalizeSudsPayload(payload, config) }
	summarizeSudsSeries(payload: EnginePayload = {}, config: ToolConfig = {}): ToolConfig[] { return summarizeSudsSeries(payload, config) }
	getMomentarySchedule(config: ToolConfig = {}): ToolConfig[] { return getMomentarySchedule(config) }
	normalizeMomentaryPayload(payload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload { return normalizeMomentaryPayload(payload, config) }
	updateMomentaryStatus(payload: EnginePayload = {}, config: ToolConfig = {}, momentId = "", status = "upcoming", now = Date.now(), optionId = ""): EnginePayload { return updateMomentaryStatus(payload, config, momentId, status, now, optionId) }
	advanceMomentarySession(payload: EnginePayload = {}, config: ToolConfig = {}, now = Date.now()): EnginePayload { return advanceMomentarySession(payload, config, now) }
	summarizeMomentarySession(payload: EnginePayload = {}, config: ToolConfig = {}): ToolConfig { return summarizeMomentarySession(payload, config) }

	createTrackerConfigFromToolConfig(config: ToolConfig = {}): ToolConfig {
		return {
			...config,
			title: config.title || "",
			instructions: config.instructions || "",
			entryLabel: config.entryLabel || "Entry",
			primaryMeasureElementId: config.primaryMeasureElementId || "",
			entryForm: config.entryForm || {
				title: config.entryLabel || "Entry",
				formElements: [],
			},
		}
	}

	createToolConfigFromTrackerConfig(trackerConfig: ToolConfig = {}): ToolConfig {
		return {
			...trackerConfig,
			title: trackerConfig.title || "",
			instructions: trackerConfig.instructions || "",
			entryLabel: trackerConfig.entryLabel || "Entry",
			primaryMeasureElementId: trackerConfig.primaryMeasureElementId || "",
			entryForm: trackerConfig.entryForm || {
				title: trackerConfig.entryLabel || "Entry",
				formElements: [],
			},
		}
	}

	createEntryFormItemFromTrackerConfig(trackerConfig: ToolConfig = {}, options: ToolEngineOptions = new ToolEngineOptions()): FormModelInterface {
		let entryForm = (trackerConfig.entryForm as ToolConfig | undefined) || {}
		return this.formEngine.createFormItemFromToolConfig({
			...entryForm,
			title: String(entryForm.title || trackerConfig.entryLabel || "Entry"),
		}, options)
	}

	createTrackerEnginePayload(enginePayload: EnginePayload = {}, trackerConfig: ToolConfig = {}, formItem: FormModelInterface | null = null): EnginePayload {
		let formPayload = this.formEngine.createFormEnginePayload({}, formItem)
		return this.normalizePayload({
			...enginePayload,
			tracker: this.createToolConfigFromTrackerConfig(trackerConfig),
			entries: [
				{
					submittedAt: Date.now(),
					form: formPayload.form,
					answers: formPayload.answers,
					scoreResult: formPayload.scoreResult,
				},
			],
		}, trackerConfig)
	}

	syncTrackerConfigFromEntryForm(trackerConfig: ToolConfig = {}, formItem: FormModelInterface | null = null): ToolConfig {
		let result = this.createTrackerConfigFromToolConfig(trackerConfig)
		result.entryForm = this.formEngine.createToolConfigFromFormItem(formItem)
		let entryForm = result.entryForm as ToolConfig
		entryForm.title = String(entryForm.title || result.entryLabel || "")
		return result
	}

	createTrackerResultConfig(toolResult: ToolResultSnapshotData | null = null, toolInstance: ToolInstanceSnapshotData | null = null): ToolConfig {
		let summary = (toolResult?.summary as ToolConfig | undefined) || {}
		return this.createTrackerConfigFromToolConfig((summary.tracker as ToolConfig | undefined) || toolInstance?.config || {})
	}

	getTrackerResultEntry(toolResult: ToolResultSnapshotData | null = null): ToolConfig {
		let summary = (toolResult?.summary as ToolConfig | undefined) || {}
		let enginePayload = (toolResult?.enginePayload as ToolConfig | undefined) || {}
		let summaryEntries = (summary.entries as ToolConfig[] | undefined) || []
		let payloadEntries = (enginePayload.entries as ToolConfig[] | undefined) || []
		return summaryEntries[0] || payloadEntries[0] || {}
	}

	getPrimaryMeasureValue(entry: ToolConfig = {}, trackerConfig: ToolConfig = {}): ToolEngineDataValue {
		let answers = (entry.answers as ToolConfig | undefined) || {}
		let primaryMeasureElementId = String(trackerConfig.primaryMeasureElementId || "")
		let result = null
		if (primaryMeasureElementId) {
			result = answers[primaryMeasureElementId] ?? null
		}
		return result
	}

	createWeeklyDiaryConfigFromToolConfig(config: ToolConfig = {}): ToolConfig {
		return {
			...config,
			title: config.title || "",
			instructions: config.instructions || "",
			entryLabel: config.entryLabel || "Day",
			trackerKind: config.trackerKind || "weeklyDiary",
			weekLength: Number(config.weekLength) || 7,
			periodStartDate: String(config.periodStartDate || this.createDefaultPeriodStartDate()),
			periodAnchorLabel: this.createPeriodAnchorLabel(config),
			periodRangeLabel: this.createPeriodRangeLabel(config),
			ratingScaleMax: Math.min(Math.max(Number(config.ratingScaleMax) || 5, 3), 10),
			days: this.createWeeklyDiaryDays(config),
			sectionLabels: config.sectionLabels || {},
			weeklyFields: this.createWeeklyDiaryConfigItems(config.weeklyFields),
			ratingSections: this.createWeeklyDiaryRatingSections(config.ratingSections),
			targetBehaviors: this.createWeeklyDiaryConfigItems(config.targetBehaviors),
			contextFields: this.createWeeklyDiaryConfigItems(config.contextFields),
			skills: this.createWeeklyDiaryConfigItems(config.skills),
			includeNotes: config.includeNotes !== false,
			includeSessionEntryRatings: !!config.includeSessionEntryRatings,
			sessionEntryRatings: Array.isArray(config.sessionEntryRatings) ? config.sessionEntryRatings : [],
			reviewNotesLabel: config.reviewNotesLabel || "Private practitioner notes",
		}
	}

	createToolConfigFromWeeklyDiaryConfig(weeklyDiaryConfig: ToolConfig = {}): ToolConfig {
		return this.createWeeklyDiaryConfigFromToolConfig(weeklyDiaryConfig)
	}

	createWeeklyDiaryRatingSections(sections: ToolEngineDataValue = []): ToolConfig[] {
		let sourceSections = Array.isArray(sections) ? sections as ToolConfig[] : []
		return sourceSections.map(section => ({
			...section,
			items: this.createWeeklyDiaryConfigItems(section.items),
		}))
	}

	createWeeklyDiaryConfigItems(items: ToolEngineDataValue = []): ToolConfig[] {
		let sourceItems = Array.isArray(items) ? items as ToolConfig[] : []
		return sourceItems.map(item => ({...item}))
	}

	createWeeklyDiaryEnginePayload(enginePayload: EnginePayload = {}, trackerConfig: ToolConfig = {}, entries: ToolConfig[] = []): EnginePayload {
		let weeklyConfig = this.createWeeklyDiaryConfigFromToolConfig(trackerConfig)
		return this.normalizePayload({
			...enginePayload,
			tracker: this.createToolConfigFromWeeklyDiaryConfig(weeklyConfig),
			weeklyAnswers: enginePayload.weeklyAnswers || {},
			entries: entries.map((entry, index) => this.createWeeklyDiaryEntry(weeklyConfig, entry, index)),
		}, weeklyConfig)
	}

	createWeeklyDiaryEntry(trackerConfig: ToolConfig = {}, entry: ToolConfig = {}, index: number = 0): ToolConfig {
		let days = this.createWeeklyDiaryDays(trackerConfig)
		let day = days[index] || {}
		let answers = (entry.answers as ToolConfig | undefined) || {}
		return {
			dayId: entry.dayId || day.id || `day${index + 1}`,
			dayLabel: day.label || entry.dayLabel || `Day ${index + 1}`,
			dateLabel: day.dateLabel || entry.dateLabel || "",
			date: day.date || entry.date || "",
			updatedAt: entry.updatedAt || null,
			submittedAt: entry.submittedAt || null,
			isComplete: !!entry.isComplete,
			answers: {
				ratings: answers.ratings || {},
				targetBehaviors: answers.targetBehaviors || {},
				contextFields: answers.contextFields || {},
				skills: answers.skills || {},
				notes: answers.notes || "",
				sessionEntryRatings: answers.sessionEntryRatings || {},
			},
		}
	}

	getWeeklyDiaryResultEntries(toolResult: ToolResultSnapshotData | null = null): ToolConfig[] {
		let summary = (toolResult?.summary as ToolConfig | undefined) || {}
		let enginePayload = (toolResult?.enginePayload as ToolConfig | undefined) || {}
		let summaryEntries = (summary.entries as ToolConfig[] | undefined) || []
		let payloadEntries = (enginePayload.entries as ToolConfig[] | undefined) || []
		return summaryEntries.length ? summaryEntries : payloadEntries
	}

	createWeeklyDiaryDays(config: ToolConfig = {}): ToolConfig[] {
		let configuredDays = (Array.isArray(config.days) ? config.days : []) as ToolConfig[]
		let weekLength = Number(config.weekLength) || 7
		let defaultLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
		let result = []
		for (let index = 0; index < weekLength; index++) {
			let configuredDay = configuredDays[index] || {}
			let periodDate = this.addDaysToDateOnly(String(config.periodStartDate || this.createDefaultPeriodStartDate()), index)
			result.push({
				id: configuredDay.id || `day${index + 1}`,
				label: this.createDayLabel(periodDate, String(configuredDay.label || defaultLabels[index] || `Day ${index + 1}`)),
				dateLabel: configuredDay.dateLabel || this.createDateLabel(periodDate),
				date: periodDate || "",
			})
		}
		return result
	}

	createDefaultPeriodStartDate(): string {
		let today = new Date()
		let day = today.getDay()
		let mondayOffset = day === 0 ? -6 : 1 - day
		let monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + mondayOffset)
		return this.formatDateOnly(monday)
	}

	createPeriodRangeLabel(config: ToolConfig = {}): string {
		let startDate = String(config.periodStartDate || this.createDefaultPeriodStartDate())
		let weekLength = Number(config.weekLength) || 7
		let endDate = this.addDaysToDateOnly(startDate, Math.max(weekLength - 1, 0))
		let result = ""
		if (startDate && endDate) {
			result = `${this.createDateLabel(startDate)}–${this.createFullDateLabel(endDate)}`
		}
		return result
	}

	createPeriodAnchorLabel(config: ToolConfig = {}): string {
		let startDate = String(config.periodStartDate || this.createDefaultPeriodStartDate())
		let result = ""
		if (startDate) {
			result = `Week of ${this.createFullDateLabel(startDate)}`
		}
		return result
	}

	createDayLabel(dateOnly: string = "", fallback: string = ""): string {
		let result = fallback
		let date = this.parseDateOnly(dateOnly)
		if (date) {
			result = date.toLocaleDateString("en-US", {weekday: "short"})
		}
		return result
	}

	createDateLabel(dateOnly: string = ""): string {
		let result = ""
		let date = this.parseDateOnly(dateOnly)
		if (date) {
			result = date.toLocaleDateString("en-US", {month: "short", day: "numeric"})
		}
		return result
	}

	createFullDateLabel(dateOnly: string = ""): string {
		let result = ""
		let date = this.parseDateOnly(dateOnly)
		if (date) {
			result = date.toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"})
		}
		return result
	}

	addDaysToDateOnly(dateOnly: string = "", dayCount: number = 0): string {
		let result = ""
		let date = this.parseDateOnly(dateOnly)
		if (date) {
			result = this.formatDateOnly(new Date(date.getFullYear(), date.getMonth(), date.getDate() + dayCount))
		}
		return result
	}

	parseDateOnly(dateOnly: string = ""): Date | null {
		let result: Date | null = null
		let match = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/)
		if (match) {
			result = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
		}
		return result
	}

	formatDateOnly(date: Date): string {
		let year = date.getFullYear()
		let month = `${date.getMonth() + 1}`.padStart(2, "0")
		let day = `${date.getDate()}`.padStart(2, "0")
		return `${year}-${month}-${day}`
	}

}
